import { alpha, createTheme } from '@mui/material';
import { themeFontSizes } from './fontSizes';

declare module '@mui/material/styles' {
  interface TypeBackground {
    lighter: string;
    darker: string;
    popover: string;
    main: string;
    infoRow: string;
    buy: string;
    buyHover: string;
    buyDark: string;
    sell: string;
    sellHover: string;
    sellDark: string;
    lightHover?: string;
    uatBanner: string;
    boxShadow: string;
    showCardBackground: string;
  }

  interface TypographyVariants {
    fontWeightExtraBold: number;
    input: {
      fontSize: number;
    };
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    fontWeightExtraBold?: number;
    input: {
      fontSize: number;
    };
  }

  interface TypeText {
    input?: string;
    header?: string;
    dark?: string;
  }

  interface PaletteOptions {
    border?: string;
  }

  interface TypeAction {
    inputHover?: string;
  }

  interface Palette {
    border: string;
  }
}

let theme = createTheme({
  typography: {
    fontFamily: ['Noto Sans'].join(','),
    fontWeightExtraBold: 900,
    input: {
      fontSize: 11,
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#4189E8', //blue
    },
    secondary: {
      main: '#282B31', //grey
    },
    background: {
      default: '#1D1F28', //dark grey
      lighter: '#282A33', //grey
      darker: '#12141A', //dark grey
      popover: '#292B33', //grey
      main: '#010101', //black
      infoRow: '#9ba0a3', //grey
      buy: 'hsl(217, 60%, 50%)', //blue
      buyHover: 'hsl(217, 40%, 50%)',
      buyDark: 'hsl(217, 80%, 10%)',
      sell: 'hsl(0, 60%, 50%)', //red
      sellHover: 'hsl(0, 40%, 50%)',
      sellDark: 'hsl(0, 80%, 7%)',
      lightHover: '#65686e', //grey hover
      uatBanner: '#E67E22', //orange
      boxShadow: 'hsla(0, 0%, 0%, 0.3)',
      showCardBackground: 'hsla(0, 0%, 0%, 0.1)',
    },
    success: {
      main: '#09FB9D', //green
      dark: 'hsl(157, 60%, 50%) !important',
    },
    warning: {
      main: '#FF9900', //yellow
      light: '#ffbb00', //yellow light
    },
    error: {
      main: '#FF6D46',
    },
    text: {
      primary: '#FFFFF2', //white
      disabled: '#999999', //grey
      secondary: '#999999', //grey
      input: '#FFFFF2', //white
      dark: '#000000', //black
    },
    border: '#F3FFFF', //white
    action: {
      hover: '#7ba8e3', //blue hover
      inputHover: '#232933', //grey
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: 'none',
          '&:hover': {
            color: theme.palette.text.primary,
            backgroundColor: `${theme.palette.action.hover}`,
          },
          // TODO: get rid of the "!important" statements below. If everything is important,
          // nothing is important. Do not use "important!" like this!
          '&:focus': {
            color: theme.palette.text.primary,
            backgroundColor: `${theme.palette.action.hover} !important`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: `${theme.palette.action.hover} !important`,
          },
          '&:focus-visible': {
            borderColor: `${theme.palette.primary.main} !important`,
          },
          '&.Mui-focusVisible': {
            borderColor: `${theme.palette.primary.main} !important`,
          },
          '&.Mui-disabled': {
            color: `${alpha(theme.palette.text.primary, 0.3)} !important`,
          },
          '&.MuiButton-outlinedError:hover': {
            backgroundColor: `${theme.palette.background.sellHover} !important`,
          },
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: 'none',
          '&:hover': {
            color: theme.palette.text.primary,
            backgroundColor: `${theme.palette.action.hover} !important`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: `${theme.palette.action.hover} !important`,
          },
          '&:focus-visible': {
            borderColor: `${theme.palette.primary.main} !important`,
          },
          '&.Mui-focusVisible': {
            borderColor: `${theme.palette.primary.main} !important`,
          },
          '&.Mui-disabled': {
            color: `${alpha(theme.palette.text.primary, 0.3)} !important`,
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          transition: 'none',
          '&:hover': {
            color: theme.palette.text.primary,
            backgroundColor: 'unset',
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: `0 2px 10px 0 ${alpha(theme.palette.common.black, 0.25)}`,
          // TODO: remove this, it's wrecking the default MUI transition functionality
          // and the commit message doesn't explain what it was originally fixing.
          transition: 'none !important',
        }),
      },
    },
    MuiCollapse: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 'fit-content !important',
          maxHeight: '350px !important',
          //add easy ease transition to colapse
          transition: 'ease-in-out 0.3s !important',
          overflowY: 'auto',
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: 40,
          boxSizing: 'border-box',
          '&:nth-of-type(even)': {
            '&:hover': {
              backgroundColor: '#1C1E25', //dark grey
            },
          },
          '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.background.lighter,
            '&:hover': {
              backgroundColor: '#32343F', //dark grey
            },
          },
          '&:first-of-type > td:first-of-type': {
            borderTopLeftRadius: theme.shape.borderRadius,
          },
          '&:first-of-type > td:last-of-type': {
            borderTopRightRadius: theme.shape.borderRadius,
          },
        }),
      },
    },
  },
});

theme = themeFontSizes(theme);

export default theme;
